/* freebar-v1  |  assets/cw-freebar.js
 *
 * One job: keep the free 110 g bar in step with the 500 ml shampoo in the cart.
 * It is inert outside the promo window because the snippet that loads this file
 * is itself date gated, and because every entry point below re-checks
 * CW_FREEBAR.endMs against the clock.
 *
 * 2026-09-25: the promo dialog and the theme level suppression of the email
 * signup popup were removed at the client's request. The client now runs site
 * popups from its email platform, so this file no longer touches any signup form.
 *
 * The pricing is NOT done here. Two automatic Buy X Get Y discounts take the bar
 * to $0. This file only makes sure the right bar is in the cart, so if the script
 * never runs the customer simply does not get the gift line added for them, and
 * nothing is ever mispriced.
 */
(function () {
  'use strict';

  var C = window.CW_FREEBAR;
  if (!C || !C.pairs || !C.pairs.length) return;
  if (!C.endMs || Date.now() >= C.endMs) return;

  var GIFT_PROP = C.giftProp;
  var GIFT_VALUE = C.giftValue;

  function log() { /* deliberately silent in production */ }

  function ss(key, val) {
    try {
      if (val === undefined) return window.sessionStorage.getItem(key);
      window.sessionStorage.setItem(key, val);
    } catch (e) { return null; }
  }

  /* ------------------------------------------------------------------ cart */

  var busy = false;      // a write of ours is in flight
  var queued = false;    // something asked for another pass while we were busy
  var timer = null;

  function dismissKey(giftId) { return 'cwfb_off_' + giftId; }
  function isDismissed(giftId) { return ss(dismissKey(giftId)) === '1'; }
  function dismiss(giftId) { ss(dismissKey(giftId), '1'); }

  function isGiftLine(item) {
    return !!(item.properties && item.properties[GIFT_PROP] === GIFT_VALUE);
  }

  function qtyOfVariant(cart, id) {
    var n = 0;
    cart.items.forEach(function (it) { if (it.variant_id === id) n += it.quantity; });
    return n;
  }

  function qtyOfGift(cart, id) {
    var n = 0;
    cart.items.forEach(function (it) {
      if (it.variant_id === id && isGiftLine(it)) n += it.quantity;
    });
    return n;
  }

  /* Whether to ADD a gift bar is decided against EVERY unit of that bar in the
   * cart, not only the ones we flagged. Measured on the live store 2026-09-18:
   * with a bar the customer chose plus a flagged bar of the same variant in the
   * cart, Shopify is free to apply the Buy X Get Y to either line, and it picked
   * the customer's. The cart total was right but the line labelled "Free with
   * your 500ml shampoo" showed $19.00 while an unlabelled line showed $0.00.
   * Counting every unit means we never create that second line: the bar the
   * customer already put in the cart becomes the free one, which is the same
   * money and a cart that reads honestly. Trimming still only ever touches our
   * own flagged lines, so a bar they paid for is never reduced or removed. */

  function post(url, body, wantSections) {
    if (wantSections) {
      var t = renderTargets();
      if (t.length) {
        body.sections = t.map(function (x) { return x.section; }).join(',');
        body.sections_url = window.location.pathname;
      }
    }
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function getCart() {
    return fetch(C.routes.cart + '.js', {
      headers: { Accept: 'application/json' }, cache: 'no-store'
    }).then(function (r) { return r.json(); });
  }

  function reconcile() {
    if (Date.now() >= C.endMs) return Promise.resolve();
    if (busy) { queued = true; return Promise.resolve(); }
    busy = true;

    return getCart().then(function (cart) {
      if (!cart || !cart.items) return null;

      var adds = [];
      var trims = [];

      C.pairs.forEach(function (p) {
        var need = qtyOfVariant(cart, p.shampooId);      // 500 ml bottles
        var haveAll = qtyOfVariant(cart, p.giftId);      // every bar of that scent
        var haveOurs = qtyOfGift(cart, p.giftId);        // the ones we added

        if (haveAll > need && haveOurs > 0) {
          // Too many bars and some of them are ours. Give back only our own,
          // never a unit the customer chose.
          var excess = Math.min(haveOurs, haveAll - need);
          cart.items.forEach(function (it) {
            if (excess <= 0) return;
            if (it.variant_id !== p.giftId || !isGiftLine(it)) return;
            var keep = it.quantity - excess;
            if (keep < 0) keep = 0;
            excess -= (it.quantity - keep);
            trims.push({ key: it.key, quantity: keep });
          });
        } else if (need > haveAll && !isDismissed(p.giftId)) {
          adds.push({
            id: p.giftId,
            quantity: need - haveAll,
            properties: (function () { var o = {}; o[GIFT_PROP] = GIFT_VALUE; return o; })()
          });
        }
      });

      if (!adds.length && !trims.length) return null;

      var chain = Promise.resolve();
      trims.forEach(function (t, i) {
        var last = (i === trims.length - 1) && !adds.length;
        chain = chain.then(function () {
          return post(C.routes.change + '.js', { id: t.key, quantity: t.quantity }, last);
        });
      });
      if (adds.length) {
        chain = chain.then(function () {
          return post(C.routes.add + '.js', { items: adds }, true);
        });
      }
      return chain;
    }).then(function (state) {
      if (state && state.sections) return paint(state.sections);
      return null;
    }).catch(function () {
      // A cart endpoint can answer 429 with an interstitial. Stay quiet and let
      // the next cart event try again rather than breaking the page.
      return null;
    }).then(function () {
      busy = false;
      if (queued) { queued = false; schedule(120); }
    });
  }

  function schedule(ms) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () { timer = null; reconcile(); },
                       typeof ms === 'number' ? ms : 250);
  }

  /* ------------------------------------------------------------- rendering */

  function renderTargets() {
    var out = [];
    var drawer = document.getElementById('CartDrawer');
    if (drawer) out.push({ section: 'cart-drawer', sel: '#CartDrawer', host: null });
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble) out.push({ section: 'cart-icon-bubble', sel: '.shopify-section', host: bubble });
    var items = document.getElementById('main-cart-items');
    if (items) out.push({ section: items.dataset.id || 'main-cart-items',
                          sel: '.js-contents', host: items });
    var footer = document.getElementById('main-cart-footer');
    if (footer) out.push({ section: footer.dataset.id || 'main-cart-footer',
                           sel: '.js-contents', host: footer });
    return out;
  }

  function paint(sections) {
    renderTargets().forEach(function (t) {
      var html = sections[t.section];
      if (!html) return;
      var doc;
      try { doc = new DOMParser().parseFromString(html, 'text/html'); }
      catch (e) { return; }
      var src = doc.querySelector(t.sel);
      if (!src) return;
      var dst;
      if (t.sel === '#CartDrawer') dst = document.getElementById('CartDrawer');
      else dst = (t.host && t.host.querySelector(t.sel)) || t.host;
      if (!dst) return;
      dst.innerHTML = src.innerHTML;
    });
    // The theme's own cart count lives outside the sections above on some pages.
    var drawerEl = document.querySelector('cart-drawer');
    if (drawerEl) {
      var any = document.querySelector('#CartDrawer .cart-item');
      drawerEl.classList.toggle('is-empty', !any);
    }
    return null;
  }

  /* -------------------------------------------------------- cart listeners */

  // A gift line the customer removes by hand must not come straight back. The
  // listener runs in the capture phase so the flag is set before the theme's
  // own remove handler fires and the line disappears.
  document.addEventListener('click', function (ev) {
    var row = ev.target && ev.target.closest && ev.target.closest('[data-cw-freebar-gift]');
    if (!row) return;
    if (!ev.target.closest('cart-remove-button, .button-cart-item-remove, [name="minus"]')) return;
    var id = parseInt(row.getAttribute('data-cw-freebar-gift'), 10);
    if (id) dismiss(id);
  }, true);

  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = '';
    try { url = (typeof input === 'string') ? input : (input && input.url) || ''; }
    catch (e) { url = ''; }
    var p = nativeFetch.apply(this, arguments);
    if (!busy && /\/cart\/(add|change|update|clear)/.test(url)) {
      p.then(function () { schedule(); }, function () {});
    }
    return p;
  };

  var nativeOpen = window.XMLHttpRequest && window.XMLHttpRequest.prototype.open;
  if (nativeOpen) {
    window.XMLHttpRequest.prototype.open = function (method, url) {
      try {
        if (typeof url === 'string' && /\/cart\/(add|change|update|clear)/.test(url)) {
          this.addEventListener('load', function () { if (!busy) schedule(); });
        }
      } catch (e) {}
      return nativeOpen.apply(this, arguments);
    };
  }

  function bootCart() {
    if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      try {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (e) {
          if (e && e.source === 'cw-freebar') return;
          if (!busy) schedule();
        });
      } catch (e) {}
    }
    schedule(400);
  }

  /* ----------------------------------------------------------------- start */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootCart);
  } else {
    bootCart();
  }

  window.CW_FREEBAR.reconcile = reconcile;
})();
